"use client";

import Link from "next/link";
import { useActionState } from "react";
import { Field, FormAlert, SubmitButton } from "@/components/form";
import { initialActionState } from "@/lib/action-state";
import {
  forgotPasswordAction,
  resendVerificationAction,
  resetPasswordAction,
  signInAction,
  signUpAction,
  verifyTwoFactorAction,
} from "../actions";

export function SignInForm({ next }: { next?: string }) {
  const [state, action] = useActionState(signInAction, initialActionState);
  return (
    <form action={action} className="flex flex-col gap-5" noValidate>
      <FormAlert state={state} />
      <input type="hidden" name="next" value={next ?? ""} />
      <Field name="email" label="Email" type="email" autoComplete="email" required state={state} />
      <Field
        name="password"
        label="Kata sandi"
        type="password"
        autoComplete="current-password"
        required
        state={state}
      />
      <div className="-mt-2 text-right text-sm">
        <Link href="/lupa-kata-sandi" className="font-semibold text-primary">
          Lupa kata sandi?
        </Link>
      </div>
      <SubmitButton size="lg" pendingText="Memeriksa…">
        Masuk
      </SubmitButton>
    </form>
  );
}

export function TwoFactorForm({ mode, next }: { mode: "totp" | "backup"; next?: string }) {
  const [state, action] = useActionState(verifyTwoFactorAction, initialActionState);
  return (
    <form action={action} className="flex flex-col gap-5" noValidate>
      <FormAlert state={state} />
      <input type="hidden" name="mode" value={mode} />
      <input type="hidden" name="next" value={next ?? ""} />
      {mode === "totp" ? (
        <Field
          name="code"
          label="Kode 6 digit"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          pattern="[0-9]*"
          className="[&_input]:num [&_input]:text-center [&_input]:text-xl [&_input]:tracking-[0.4em]"
          autoFocus
          required
          state={state}
        />
      ) : (
        <Field name="code" label="Kode cadangan" autoComplete="off" autoFocus required state={state} />
      )}
      <SubmitButton size="lg" pendingText="Memverifikasi…">
        Verifikasi
      </SubmitButton>
    </form>
  );
}

export function SignUpForm({ next, email }: { next?: string; email?: string }) {
  const [state, action] = useActionState(signUpAction, initialActionState);
  return (
    <form action={action} className="flex flex-col gap-5" noValidate>
      <FormAlert state={state} />
      <input type="hidden" name="next" value={next ?? ""} />
      <Field name="name" label="Nama lengkap" autoComplete="name" required state={state} />
      <Field
        name="email"
        label="Email kerja"
        type="email"
        autoComplete="email"
        defaultValue={email}
        readOnly={!!email}
        required
        state={state}
      />
      <Field
        name="password"
        label="Kata sandi"
        type="password"
        autoComplete="new-password"
        hint="Minimal 10 karakter, kombinasi huruf dan angka."
        required
        state={state}
      />
      <Field
        name="confirmPassword"
        label="Ulangi kata sandi"
        type="password"
        autoComplete="new-password"
        required
        state={state}
      />
      <SubmitButton size="lg" pendingText="Mendaftarkan…">
        Daftar
      </SubmitButton>
    </form>
  );
}

export function ResendVerificationForm({ email }: { email: string }) {
  const [state, action] = useActionState(resendVerificationAction, initialActionState);
  return (
    <form action={action} className="flex flex-col gap-4">
      <FormAlert state={state} />
      <input type="hidden" name="email" value={email} />
      <SubmitButton variant="outline" pendingText="Mengirim…">
        Kirim ulang email verifikasi
      </SubmitButton>
    </form>
  );
}

export function ForgotPasswordForm() {
  const [state, action] = useActionState(forgotPasswordAction, initialActionState);
  if (state.ok) return <FormAlert state={state} />;
  return (
    <form action={action} className="flex flex-col gap-5" noValidate>
      <FormAlert state={state} />
      <Field name="email" label="Email" type="email" autoComplete="email" required state={state} />
      <SubmitButton size="lg" pendingText="Mengirim…">
        Kirim tautan
      </SubmitButton>
    </form>
  );
}

export function ResetPasswordForm({ token }: { token: string }) {
  const [state, action] = useActionState(resetPasswordAction, initialActionState);
  return (
    <form action={action} className="flex flex-col gap-5" noValidate>
      <FormAlert state={state} />
      <input type="hidden" name="token" value={token} />
      <Field
        name="password"
        label="Kata sandi baru"
        type="password"
        autoComplete="new-password"
        hint="Minimal 10 karakter, kombinasi huruf dan angka."
        required
        state={state}
      />
      <Field
        name="confirmPassword"
        label="Ulangi kata sandi baru"
        type="password"
        autoComplete="new-password"
        required
        state={state}
      />
      <SubmitButton size="lg" pendingText="Menyimpan…">
        Simpan kata sandi
      </SubmitButton>
    </form>
  );
}
